import React, {useRef, useState, useMemo} from 'react';

import moment from 'moment'
import Grid from "@material-ui/core/Grid";

import TextField from "@material-ui/core/TextField/TextField";
import Popover from '@material-ui/core/Popover';

import { addDays, startOfDay, endOfDay, startOfMonth, endOfMonth, addMonths, startOfWeek, endOfWeek, isSameDay, differenceInCalendarDays } from 'date-fns';
import {DateRangePicker, createStaticRanges, InputRange, RangeFocus} from 'react-date-range';
import { ja } from 'date-fns/locale'
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

/**
 * 日付形式に変換して返す
 * @param {moment.Moment} momentInstance
 * @returns {string}
 */
const toUtcIso8601str = (momentInstance: moment.Moment) => {
  return momentInstance
    .clone()
    // .utc()
    .format('YYYY-MM-DD')
}

const defineds = {
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfToday: startOfDay(new Date()),
  endOfToday: endOfDay(new Date()),
  startOfYesterday: startOfDay(addDays(new Date(), -1)),
  endOfYesterday: endOfDay(addDays(new Date(), -1)),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
  endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
  startOfLast7Days: startOfDay(addDays(new Date(), -7)),
  endOfLast7Days: endOfDay(addDays(new Date(), -1)),
  startOfLast14Days: startOfDay(addDays(new Date(), -14)),
  endOfLast14Days: endOfDay(addDays(new Date(), -1)),
  startOfLast30Days: startOfDay(addDays(new Date(), -30)),
  endOfLast30Days: endOfDay(addDays(new Date(), -1)),
  targetMinDay : startOfDay(new Date("2022-02-01T00:00:00")),
  targetMaxDay : endOfMonth(new Date(moment().endOf("month").format("YYYY-MM-DDTHH:mm:ss"))),
};

const defaultStaticRanges = createStaticRanges([
  // @ts-ignore
  {
    label: '今日',
    range: () => ({
      startDate: defineds.startOfToday,
      endDate: defineds.endOfToday,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '昨日',
    range: () => ({
      startDate: defineds.startOfYesterday,
      endDate: defineds.endOfYesterday,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '今週(日〜土)',
    range: () => ({
      startDate: defineds.startOfWeek,
      endDate: defineds.endOfWeek,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '過去7日間',
    range: () => ({
      startDate: defineds.startOfLast7Days,
      endDate: defineds.endOfLast7Days,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '先週(日〜土)',
    range: () => ({
      startDate: defineds.startOfLastWeek,
      endDate: defineds.endOfLastWeek,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '過去14日間',
    range: () => ({
      startDate: defineds.startOfLast14Days,
      endDate: defineds.endOfLast14Days,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '今月',
    range: () => ({
      startDate: defineds.startOfMonth,
      endDate: defineds.endOfMonth,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '過去30日間',
    range: () => ({
      startDate: defineds.startOfLast30Days,
      endDate: defineds.endOfLast30Days,
      key: "range",
      isReset: false,
    }),
  },
  // @ts-ignore
  {
    label: '先月',
    range: () => ({
      startDate: defineds.startOfLastMonth,
      endDate: defineds.endOfLastMonth,
      key: "range",
      isReset: false,
    }),
  },
]);

const defaultInputRanges: InputRange[] = [
  // @ts-ignore
  {
    label: "日間(今日まで)",
    range(value) {
      console.log(value);
      return {
        startDate: addDays(defineds.startOfToday, (Math.max(Number(value), 1) - 1) * -1),
        endDate: defineds.endOfToday,
        key: "range",
        isReset: Number(value) === 0,
      };
    },
    getCurrentValue(range: any) {
      if (!range.endDate || !isSameDay(range.endDate, defineds.endOfToday) || range.isReset) return '';
      if (!range.startDate) return '∞';
      return String(differenceInCalendarDays(defineds.endOfToday, range.startDate) + 1);
    },
  },
  // @ts-ignore
  {
    label: "日間(昨日まで)",
    range(value) {
      console.log(value);
      return {
        startDate: addDays(defineds.startOfYesterday, (Math.max(Number(value), 1) - 1) * -1),
        endDate: defineds.endOfYesterday,
        key: "range",
        isReset: Number(value) === 0,
      };
    },
    getCurrentValue(range: any) {
      if (!range.endDate || !isSameDay(range.endDate, defineds.endOfYesterday) || range.isReset) return '';
      if (!range.startDate) return '∞';
      return String(differenceInCalendarDays(defineds.endOfYesterday, range.startDate) + 1);
    },
  },
]

export const ApolloDateRangePicker: React.FC<{ startDateLabel ?: string, startDate: string, setStartDate: React.Dispatch<React.SetStateAction<string>>, startMinDate ?: Date, endDateLabel ?: string, endDate: string, setEndDate: React.Dispatch<React.SetStateAction<string>>,  endMaxDate ?: Date, hideInputRanges?: boolean,}> = ({ startDateLabel, startDate, setStartDate, startMinDate, endDate, setEndDate, endMaxDate, endDateLabel, hideInputRanges = false}) => {
  const [isSelected, setIsSelected] = React.useState<RangeFocus>([0,1]);
  const isReset = useRef<boolean>(true);
  const isDefault = useRef<boolean>(true);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const defaultStartDate: string = startDate;
  const defaultEndDate: string = endDate;
  const defaultStart = moment(defaultStartDate).startOf("day");
  const defaultEnd =  moment(defaultEndDate).startOf("day");
  const id = open ? 'simple-popover' : undefined;

  // ① 選択可能境界（下限= startMinDate 優先、上限= endMaxDate 優先）
  const minBound = startMinDate ?? defineds.targetMinDay;
  const maxBound = endMaxDate   ?? defineds.targetMaxDay;

  // ② staticRanges を境界内のものだけに絞る（範囲外は非表示）
  const filteredStaticRanges = React.useMemo(() => {
   const within = (d: Date) =>
     d >= startOfDay(minBound) && d <= endOfDay(maxBound);
   return defaultStaticRanges.filter((sr) => {
     // range は型上 引数1つ必須。中身では未使用でも {} を渡す
     const r = (sr as any).range({} as any) as {
       startDate?: Date;
       endDate?: Date;
     };
     const s = r.startDate;
     const e = r.endDate;
     if (!s || !e) return false;  // undefined ガード
     return within(s) && within(e);
   });
 }, [minBound, maxBound]);

  // ③ onChange での安全装置：範囲外は境界に丸める
  const clampToBounds = (m: moment.Moment) => {
    const d = m.toDate();
    if (d < minBound) return moment(minBound);
    if (d > maxBound) return moment(maxBound);
    return m;
  };

  const computedInputRanges: InputRange[] = useMemo(() => {
    // yesterday の end と today の end を取得
    const yesterdayEnd = defineds.endOfYesterday;
    const todayEnd = defineds.endOfToday;

    // yesterday に対する最大日数（minBound を考慮）
    const maxDaysForYesterday = Math.max(0, differenceInCalendarDays(yesterdayEnd, startOfDay(minBound)) + 1);

    // today に対する最大日数（minBound を考慮）
    const maxDaysForToday = Math.max(0, differenceInCalendarDays(todayEnd, startOfDay(minBound)) + 1);

    const ranges: InputRange[] = [
      // 日間(今日まで)
      // @ts-ignore
      {
        label: "日間(今日まで)",
        range(value) {
          const raw = Number(value);
          if (Number.isNaN(raw)) return {
            startDate: addDays(defineds.startOfToday, 0),
            endDate: defineds.endOfToday,
            key: "range",
            isReset: Number(value) === 0,
          };
          // 最小は1日、最大は maxDaysForToday
          let n = Math.max(1, Math.floor(raw));
          if (maxDaysForToday > 0 && n > maxDaysForToday) n = maxDaysForToday;
          const start = addDays(defineds.startOfToday, (n - 1) * -1);
          // さらに minBound より前にはしない
          const adjStart = start < startOfDay(minBound) ? startOfDay(minBound) : start;
          return {
            startDate: adjStart,
            endDate: defineds.endOfToday,
            key: "range",
            isReset: Number(value) === 0,
          };
        },
        getCurrentValue(range: any) {
          if (!range.endDate || !isSameDay(range.endDate, defineds.endOfToday) || range.isReset) return '';
          if (!range.startDate) return '∞';
          return String(differenceInCalendarDays(defineds.endOfToday, range.startDate) + 1);
        },
      },
      // 日間(昨日まで)
      // @ts-ignore
      {
        label: "日間(昨日まで)",
        range(value) {
          const raw = Number(value);
          if (Number.isNaN(raw)) return {
            startDate: addDays(defineds.startOfYesterday, 0),
            endDate: defineds.endOfYesterday,
            key: "range",
            isReset: Number(value) === 0,
          };
          // 最小は1日
          let n = Math.max(1, Math.floor(raw));
          // 範囲外なら最大に丸める（ユーザー要求どおり）
          if (maxDaysForYesterday > 0 && n > maxDaysForYesterday) n = maxDaysForYesterday;
          // その日数で start を算出
          const start = addDays(defineds.startOfYesterday, (n - 1) * -1);
          // さらに start が minBound より前なら minBound に丸める
          const adjStart = start < startOfDay(minBound) ? startOfDay(minBound) : start;
          return {
            startDate: adjStart,
            endDate: defineds.endOfYesterday,
            key: "range",
            isReset: Number(value) === 0,
          };
        },
        getCurrentValue(range: any) {
          if (!range.endDate || !isSameDay(range.endDate, defineds.endOfYesterday) || range.isReset) return '';
          if (!range.startDate) return '∞';
          const days = differenceInCalendarDays(defineds.endOfYesterday, range.startDate) + 1;
          // days は既に minBound の影響を受けた値なので、そのまま返す
          return String(days);
        },
      },
    ];

    return ranges;
  }, [minBound]);

  const [state, setState] = useState(
    {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      key: 'selection',
      isReset: false,
    }
  );
  const onChangeDatePicker = (e: any) => {
    let start = moment(e.selection.startDate).startOf("day");
    let end = moment(e.selection.endDate).startOf("day");
    start = clampToBounds(start);
    end   = clampToBounds(end);
    if(e.selection.key === "range") {
      setState({
        startDate: e.selection.startDate,
        endDate: e.selection.endDate,
        key: 'selection',
        isReset: e.selection.isReset,
      });
      setStartDate(toUtcIso8601str(start));
      setEndDate(toUtcIso8601str(end));
      isReset.current = false;
    }else if (isSelected[1] === 0) {
      setState(e.selection);
      isReset.current = true;
      if(!start.isSame(end)) {
        setStartDate(toUtcIso8601str(start));
        setEndDate(toUtcIso8601str(end));
        isReset.current = false;
      }
    }else {
      let tmpEndDate = new Date(endDate);
      setStartDate(toUtcIso8601str(start));
      if(start.isSameOrAfter(defaultStart) || isReset.current) {
        if(!isDefault.current) {
          tmpEndDate = e.selection.endDate;
          setEndDate(toUtcIso8601str(end));
        }else if(start.isSameOrAfter(defaultStart) && end.isSameOrAfter(defaultStart)){
          tmpEndDate = e.selection.endDate;
          setEndDate(toUtcIso8601str(end));
        }else {
          tmpEndDate = new Date(defaultEndDate);
          setEndDate(toUtcIso8601str(defaultEnd));
        }
      }
      setState({
        startDate: e.selection.startDate,
        endDate: tmpEndDate,
        key: e.selection.key,
        isReset: e.selection.isReset,
      });
      isReset.current = false;
    }
    if(isDefault.current) isDefault.current = false;
    if(!isReset.current) {
      setTimeout(() => {
        setIsSelected([0, 1]);
      }, 100);
    }
  }
  const onChangeDate = (e: any) => {
    handleClick(e)
  }

  return (
    <React.Fragment>
      <Grid container spacing={1}>
        <Grid item xs={4} >
          <TextField
              label={startDateLabel ?? ""}
              id={"startDate"}
              name={"startDate"}
              value={startDate}
              onClick={e => onChangeDate(e)}
          />
        </Grid>
        <Grid item xs={1}>
          ～
        </Grid>
        <Grid item xs={4}>
          <TextField
              label={endDateLabel ?? ""}
              id={"endDate"}
              name={"endDate"}
              value={endDate}
              onClick={e => onChangeDate(e)}
          />
        </Grid>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <DateRangePicker
            onRangeFocusChange={e => setIsSelected(e)}
            focusedRange={isSelected}
            onChange={e => onChangeDatePicker(e)}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            months={1}
            scroll={{ enabled: true }}
            ranges={[state]}
            minDate={minBound}
            maxDate={maxBound}
            direction="vertical"
            locale={ja}
            staticRanges={filteredStaticRanges}
            inputRanges={hideInputRanges ? [] : computedInputRanges}
          />
        </Popover>
      </Grid>
    </React.Fragment>
  )
}

export default ApolloDateRangePicker;
